import * as v from 'valibot'

import { NOVEL_DRAMA_END_REASONS } from './drama.js'

const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(120))
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))
const PositiveInteger = v.pipe(v.number(), v.integer(), v.minValue(1))
const Json = v.record(v.string(), v.unknown())
const RunStatus = v.picklist(['active', 'archived'])

/**
 * 后台对局列表查询。query string 的值都是字符串，页码与页大小在这里转成数字（缺省第 1 页、每页 20 条；valibot 的缺省值也要过一遍 schema，所以缺省写成字符串）；
 * `email` 按包含匹配、不区分大小写，`pack_id` 精确匹配；`failed_only=true` 只返回 failed_jobs_count > 0 的对局（C8）。
 */
export const NovelAdminRunListQuery = v.strictObject({
  email: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(320))),
  pack_id: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(64))),
  page: v.optional(v.pipe(v.string(), v.regex(/^[1-9]\d{0,5}$/), v.transform(Number)), '1'),
  page_size: v.optional(v.pipe(v.string(), v.regex(/^[1-9]\d{0,2}$/), v.transform(Number), v.maxValue(100)), '20'),
  failed_only: v.optional(v.pipe(v.picklist(['true', 'false']), v.transform((value) => value === 'true')), 'false'),
})

/**
 * 列表一行：玩家邮箱、作品标题与 slug 由服务端联表给出，用户行已不存在时邮箱为 null；
 * failed_jobs_count 是活动分支 failed 任务数（C8）。
 */
export const NovelAdminRunRow = v.object({
  id: Id,
  user_email: v.nullable(v.string()),
  pack_id: v.string(),
  pack_title: v.string(),
  pack_slug: v.string(),
  status: RunStatus,
  turn_count: Count,
  has_pending_jobs: v.boolean(),
  failed_jobs_count: Count,
  last_played_at: v.string(),
  play_language: v.string(),
  source_origin: v.string(),
})

export const NovelAdminRunList = v.object({
  items: v.array(NovelAdminRunRow),
  total: Count,
  page: PositiveInteger,
  pages: PositiveInteger,
  page_size: PositiveInteger,
})

/** 详情里的对局行：`novel_runs` 的列（不含快照）加玩家邮箱、作品标题与快照 package_hash（开局时冻结内容的哈希）。 */
export const NovelAdminRun = v.object({
  id: Id,
  user_id: v.string(),
  user_email: v.nullable(v.string()),
  pack_id: v.string(),
  pack_title: v.string(),
  package_hash: v.string(),
  status: RunStatus,
  // play 为玩家对局；comparison 为运营后台模型对比复制出的对局（C27），到 expires_at 由 worker 删除。
  purpose: v.picklist(['play', 'playtest', 'comparison']),
  play_language: v.string(),
  source_origin: v.string(),
  expires_at: v.nullable(v.string()),
  active_request_id: v.nullable(v.string()),
  turn_count: Count,
  has_pending_jobs: v.boolean(),
  job_lease_owner: v.nullable(v.string()),
  job_lease_until: v.nullable(v.string()),
  last_played_at: v.string(),
  created_at: v.string(),
  updated_at: v.string(),
})

/** 请求正文：平台审计里保存的消息数组，原样给出。 */
export const NovelAdminCallRequestBody = v.record(v.string(), v.unknown())

/** 响应正文：模型输出的文本与推理文本。 */
export const NovelAdminCallResponseBody = v.object({
  response_text: v.nullable(v.string()),
  reasoning_text: v.nullable(v.string()),
})

/**
 * 一次模型调用：归属列来自 `novel_agent_calls`，耗时、请求与响应正文、成本来自平台调用审计（`llm_call_log`），
 * 平台里查不到这次调用时这些字段全部为 null，页面标"审计正文不可用"。
 * finish_reason 与 protocol 平台审计里没有，由小说服务调用时记在归属行上（C8）；失败的尝试与存量行为 null。
 * role 取 performer（写作者）或 structured（记录、记忆、导演、建议）。
 */
export const NovelAdminCall = v.object({
  request_id: v.nullable(v.string()),
  job_id: v.nullable(v.string()),
  role: v.picklist(['performer', 'structured']),
  task: v.optional(v.nullable(v.string()), null),
  mode: v.optional(v.nullable(v.string()), null),
  model: v.nullable(v.string()),
  provider: v.nullable(v.string()),
  llm_call_id: v.nullable(v.string()),
  finish_reason: v.nullable(v.string()),
  protocol: v.nullable(v.string()),
  created_at: v.string(),
  ttft_ms: v.nullable(Count),
  first_token_ms: v.optional(v.nullable(Count), null),
  duration_ms: v.nullable(Count),
  request_body: v.nullable(NovelAdminCallRequestBody),
  response_body: v.nullable(NovelAdminCallResponseBody),
  status: v.nullable(v.picklist(['ok', 'error', 'aborted'])),
  prompt_tokens: v.nullable(Count),
  completion_tokens: v.nullable(Count),
  supplier_cost_micros: v.nullable(Count),
  currency: v.nullable(v.string()),
  charged_points: v.nullable(Count),
  error_reason: v.nullable(v.string()),
  provider_status: v.nullable(PositiveInteger),
  provider_error_code: v.nullable(v.string()),
  provider_request_id: v.nullable(v.string()),
})

/**
 * 对局详情：行字段、完整快照（原样）、作品内容、调用列表（按创建时间升序）。
 * 请求行即 `snapshot.requests` 的各条：每条带 diagnostics（该请求的校验反馈，与它的 context_record 是同一数组，C8）。
 * `pack` 是快照里冻结的作品内容 `pack_source` 经引擎规范化后的样子，也就是这局实际游玩所用的内容；后台观测页按它和快照推导场景、人物与证据。
 */
/** 一场数字人演出的玩家端指标（服务端已取整）；服务端结束的场次（过期、断开、启动失败、中断）为 null。 */
export const NovelAdminDramaMetrics = v.object({
  request_to_ready_ms: v.nullable(Count),
  request_to_first_video_ms: v.nullable(Count),
  request_to_playback_ms: v.nullable(Count),
  request_to_disconnect_ms: v.nullable(Count),
  action_count: Count,
  dialogue_count: Count,
})

/**
 * 一场数字人演出（C33），新的在前。reason 是结束原因：玩家端集合之外另有 interrupted（持有进程退出，本进程关闭或 worker 回收）；
 * refunded 为已退款（首帧前失败、到时或断开，或所属回合失败、取消的 turn_cancelled，勘误 22、24）；session_ms 是从建会话到结束的毫秒数，
 * worker 回收的场次为 null。
 */
export const NovelAdminDramaSession = v.object({
  session_id: Id,
  request_id: v.string(),
  character_id: v.string(),
  state_id: v.string(),
  live_id: v.nullable(v.string()),
  status: v.picklist(['starting', 'live', 'ended']),
  reason: v.nullable(v.picklist([...NOVEL_DRAMA_END_REASONS, 'interrupted'])),
  charged_points: Count,
  refunded: v.boolean(),
  metrics: v.nullable(NovelAdminDramaMetrics),
  started_at: v.string(),
  ready_at: v.nullable(v.string()),
  ended_at: v.nullable(v.string()),
  session_ms: v.nullable(Count),
})

export const NovelAdminRunDetail = v.object({
  run: NovelAdminRun,
  snapshot: Json,
  pack: Json,
  calls: v.array(NovelAdminCall),
  drama_sessions: v.array(NovelAdminDramaSession),
})

/** 重试一个 failed 任务的结果：`jobs` 是该任务所在分支更新后的全部任务。 */
export const NovelAdminJobRetry = v.object({
  run_id: Id,
  job_id: Id,
  has_pending_jobs: v.boolean(),
  jobs: v.array(Json),
})
